import { Customer } from '@/app/modules/p2c/customers/core/_models'
import SalesEvolutionChart from '@/app/modules/p2c/reports/product/charts/SalesEvolutionChart'
import SalesRepartitionChart from '@/app/modules/p2c/reports/product/charts/SalesRepartitionChart'
import { SalesIndicatorsData } from '@/app/modules/p2c/reports/product/core/_models'
import { getSalesIndicators } from '@/app/modules/p2c/reports/product/core/_requests'
import ProductTable from '@/app/modules/p2c/reports/product/ProductsTable'
import { Card } from '@/components/Card'
import { SmallIndicatorCard } from '@/components/cards/IndicatorCard'
import { DateRangeContext } from '@/components/context/DateRangeContext'
import React, { FC, useContext, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

interface Props {
  customer?: Customer
}

const OrderReportPane: FC<Props> = ({ customer }) => {
  const intl = useIntl()
  const { from, to, timezone } = useContext(DateRangeContext)
  const [indicators, setIndicators] = useState<SalesIndicatorsData | undefined>()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const loadIndicators = async () => {
    try {
      setIndicators(await getSalesIndicators({ from, to, userId: customer?._id, timezone }));
    } catch (e: unknown) {
      console.log(e)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadIndicators()
  }, [from, to])

  return (
    <>
      <div className="row">
        <div className="col-xl-4">
          <SalesRepartitionChart
            title="Type repartition"
            agg='type'
            userId={customer?._id}
            from={from}
            to={to}
            timezone={timezone}
            height={200}
          />
        </div>

        <div className="col-xl-4">
          <SalesRepartitionChart
            title="Category repartition"
            userId={customer?._id}
            agg='category'
            from={from}
            to={to}
            timezone={timezone}
            height={200}
          />
        </div>

        <div className="col-xl-4">
          <SalesRepartitionChart
            title="Brand repartition"
            userId={customer?._id}
            chartType='treemap'
            agg='brand'
            from={from}
            timezone={timezone}
            to={to}
            height={240}
          />
        </div>
      </div>

      <div className="mb-10">
        <div className="row justify-content-center">
          <div className="col-xl-2">
            <SmallIndicatorCard
              title="COGS"
              icon="euro"
              color="warning"
              isLoading={isLoading}
              value={
                <div className="text-end py-5">
                  {intl.formatNumber((indicators?.amount ?? 0), {
                    style: 'currency',
                    currency: 'eur',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </div>
              }
            />
          </div>

          <div className="col-xl-2">
            <SmallIndicatorCard
              title="Total order amount excl. VAT & AVG product amount"
              icon="handcart"
              color="primary"
              isLoading={isLoading}
              value={
                <>
                  <div className="text-end">
                    {intl.formatNumber(indicators?.order ?? 0)}
                  </div>
                  <div className="text-end text-white-50">
                    {intl.formatNumber((indicators?.amount ?? 0) / (indicators?.order ?? 1), {
                      style: 'currency',
                      currency: 'eur',
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </div>
                </>
              }
            />
          </div>

          <div className="col-xl-2">
            <SmallIndicatorCard
              title="Wishlisted"
              icon="like-2"
              color="danger"
              isLoading={isLoading}
              value={
                <div className="text-end py-5">
                  {indicators?.wishlisted ?? 0}
                </div>
              }
            />
          </div>
          <div className="col-xl-2">
            <SmallIndicatorCard
              title="Thumbs"
              icon="like"
              color="success"
              isLoading={isLoading}
              value={
                <>
                  <div className="text-end">
                    +{indicators?.thumbsUp ?? 0}
                  </div>
                  <div className="text-end">
                    -{indicators?.thumbsDown ?? 0}
                  </div>
                </>
              }
            />
          </div>

          <div className="col-xl-2">
            <SmallIndicatorCard
              title="Feedback %"
              icon="percentage"
              color="info"
              isLoading={isLoading}
              value={
                <div className="text-end py-5">
                  {(
                    ((indicators?.thumbsUp ?? 0) + (indicators?.thumbsDown ?? 0))
                    /
                    (((indicators?.thumbsUp ?? 0) + (indicators?.thumbsDown ?? 0) + (indicators?.noThumbs ?? 0)) || 1) * 100
                  ).toFixed(2)}%
                </div>
              }
            />
          </div>
        </div>
      </div>

      <Card title="Orders" collapsable={false}>
        <SalesEvolutionChart
          userId={customer?._id}
          from={from}
          to={to}
          timezone={timezone}
          restock={customer === undefined}
          height={400}
        />
      </Card>

      <ProductTable
        userId={customer?._id}
        from={from}
        to={to}
        timezone={timezone}
      />
    </>
  )
}

export default OrderReportPane
